/**
 * OperationHistory Component
 *
 * Main container that wires together filtering, grouping, date bucketing,
 * and the timeline view for a generic operation history feed.
 */

import type { ReactElement } from 'react';

import { IconFilterOff, IconHistory } from '@tabler/icons-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components/common/EmptyState';
import { LoadingState } from '@/components/common/loading';
import sharedStyles from '@/components/common/styles/ScrollContainerLayout.module.css';

import type {
  OperationHistoryEvent,
  OperationHistoryProps,
} from './OperationHistory.types';
import styles from './OperationHistory.module.css';
import { OperationHistoryFilters } from './components/OperationHistoryFilters';
import { OperationHistoryTimeline } from './components/OperationHistoryTimeline';
import { useOperationHistoryDateBuckets } from './hooks/useOperationHistoryDateBuckets';
import { useOperationHistoryFilter } from './hooks/useOperationHistoryFilter';
import { useOperationHistoryGrouping } from './hooks/useOperationHistoryGrouping';

const EMPTY_STATE_ICON_SIZE = 48;

export function OperationHistory<TEvent extends OperationHistoryEvent>({
  events,
  config,
  showFilters = true,
  isLoading = false,
  emptyState,
}: OperationHistoryProps<TEvent>): ReactElement | null {
  const { t } = useTranslation();

  const {
    filters,
    filteredEvents,
    setFilters,
    clearFilters,
    selectedAuthorLabels,
    availableEventTypes,
    hasActiveFilters,
  } = useOperationHistoryFilter(events);

  const { groupedEvents } = useOperationHistoryGrouping(filteredEvents);
  const dateBuckets = useOperationHistoryDateBuckets(groupedEvents);

  if (isLoading) {
    return <LoadingState size="sm" centered />;
  }

  return (
    <div className={clsx(sharedStyles.scrollContainer, styles.container)}>
      {showFilters && events.length > 0 && (
        <div className={sharedStyles.header}>
          <OperationHistoryFilters
            filters={filters}
            setFilters={setFilters}
            clearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            selectedAuthorLabels={selectedAuthorLabels}
            availableEventTypes={availableEventTypes}
            config={config}
          />
        </div>
      )}
      <div className={sharedStyles.scrollableContent}>
        {filteredEvents.length === 0 ? (
          emptyState || (
            <EmptyState
              icon={
                events.length === 0 ? (
                  <IconHistory size={EMPTY_STATE_ICON_SIZE} />
                ) : (
                  <IconFilterOff size={EMPTY_STATE_ICON_SIZE} />
                )
              }
              title={
                events.length === 0
                  ? t('history.emptyState.noHistory')
                  : t('history.emptyState.noMatches')
              }
            />
          )
        ) : (
          <OperationHistoryTimeline dateBuckets={dateBuckets} config={config} />
        )}
      </div>
    </div>
  );
}
